import { TaxConfigurationDeductions } from './configuration';
import { BaseLicense, License } from './licenses';
import { PaymentFees } from './payment';
import { EndpointData } from './store';

/** Model for The way Tax State is Handled */
export interface TaxState {
  taxPaymentPeriods: EndpointData<ExtendedPaymentPeriod>;

  isCalculateTaxInProgress: boolean;
  isCalculateTaxSuccess: boolean;
  calculatedTaxData: CalculatedTot; // raw data returned from  /calculateMultiPropertyTot
  calculatedTaxErrorMsg: string;

  preSelectedPeriod?: Period;

  taxFlowData: TaxFlowData;
  taxSteps?: unknown;

  licensesPerPeriod: Record<string, EndpointData<PeriodLicense>>;
}
export interface PeriodLicense extends License {
  address: string;
  buckets?: Array<RevenueReport>;
  occupancy?: OccupancyReport;
  ownerName: string;
  selected?: boolean;
  totPaidStatus: 'complete' | 'incomplete';
  providers: string[];
}

export interface ExtendedPaymentPeriod extends PaymentPeriod {
  year: number;
  selected?: boolean;
}

export interface PaymentPeriod extends Period {
  name: string;
  dueDate?: string;
  licenses: Array<PeriodLicense>;
  id?: string;
  quarter?: number; // used to identify the quarter in Quarterly Periods
  totalDueForPeriod?: number;
}

export enum ReportingMethod {
  INDIVIDUAL = 'individual',
  BULK = 'bulk',
}

export interface TaxFlowData {
  selectedLicenses: Array<PaymentPeriod>;
  reportingMethod?: ReportingMethod;
}

export interface Period {
  startDate: string;
  endDate: string;
}

/** Collection Agreement Data */

export interface BucketData extends CollectionAgreementBucket {
  deductionsConfig: TaxConfigurationDeductions;
}
export interface CollectionAgreementBucket {
  key: string;
  label: string;
  collected_taxes: string[];
}

export interface CollectionAgreementBucketWith {
  [date: string]: {
    buckets: CollectionAgreementBucket[];
  };
}

export interface CollectionAgreementBucketConfig {
  enable: boolean;
  with: CollectionAgreementBucketWith;
}

export interface RevenueReport {
  discounts?: number;
  revenue?: number;
  provider?: string;
}

export interface OccupancyReport {
  available?: number;
  occupied?: number;
}

export interface TaxReportLicense {
  buckets: Array<RevenueReport>;
  occupancy: OccupancyReport;
}

export interface TaxPeriod {
  licenses: Array<TaxReportLicense>;
}

export interface TaxDataModel {
  periods?: Array<TaxPeriod>;
}

export interface BucketError {
  discounts?: string;
  revenue?: string;
}

export interface OccupancyError {
  available?: string;
  occupied?: string;
}

export interface TaxLicenseError {
  buckets: Array<BucketError>;
  occupancy: OccupancyError;
}

export interface TaxPeriodError {
  licenses: Array<TaxLicenseError>;
}

export interface TaxDataError {
  periods?: Array<TaxPeriodError>;
}

export interface TaxableReceipt extends Record<string, number | undefined> {
  accruedInterest: number;
  latePenalties: number;
  total: number;
  totDue: number;
  credit?: number;
}

export interface TaxableActivity {
  deductions: number;
  key: string;
  sales: number;
  taxableReceipts: number;
  taxTypes: Record<string, TaxableReceipt>;
  totalDueForPeriod?: number;
}

export interface CalculatedTaxableActivity extends TaxableActivity {
  allowableDeductions: number;
  licenseNumber: string;
  address: string;
  totalDueForPeriod: number;
  provider: string;
  totalRevenue: number;
}

export interface CalculatedTotMisc {
  taxableActivities: Array<CalculatedTaxableActivity>;
}

export interface CalculatedTotDetail extends Period {
  misc: CalculatedTotMisc;
  licence_no: string;
  period: string;
}
export interface CalculatedTot extends PaymentFees {
  paymentId?: string;
  details: Array<CalculatedTotDetail>;
}

export interface CalculatedPeriodLicense
  extends Omit<BaseLicense, 'applicationNumber' | 'status'> {
  totalDueForPeriod?: number;
  providers: TaxableActivity[];
}

export interface CalculatedLicense extends Period {
  address?: string;
  label: string;
  licenses: CalculatedPeriodLicense[];
  providers: TaxableActivity[];
  licenseNumber?: string;
  totalDueForPeriod?: number;
}
